import { defineStore } from 'pinia'
import type { AxiosResponse } from 'axios'
import { getBaseUrlForSpaceId } from '@/config'
import { client, managementApiClient } from '@/services/client'
import { handleRequest, handleRequestOpenApi } from '@/services/handleRequest'
import type { SafeAxiosResponse } from '@/services/handleRequest'
import { usePipelinesStore } from '@/store/modules/App/pipelines'
import { useSpace } from '@/store/modules/App/space'
import type {
  Story,
  StoriesResponse,
  Breadcrumb,
  BreadcrumbsResponse,
  PresetFilter,
  BlockSchema,
  Release,
  Releases,
  UnpublishedDependencies,
  ReferenceCountsResponse,
  Language,
} from '@/types'
import type { FieldTypeSchema } from '@/types/components'
import type { BlockContent } from '@/types/content'
import type {
  BulkRestoreResponse,
  CreateContentForm,
  TranslatedSlugs,
} from '@/types/stories'
import { isEmpty } from '@utils/checkers'
import Throttle from '@utils/promiseThrottle'
import { chunk, unique } from '@/utils/arrays'
import { hasProp, isArray, isBoolean } from '@/utils/checkers/validation'
import { numberFromString } from '@/utils/parsers'
import { versionOperator2ChangeSymbol } from '@/components/ContentManager/EditorPage/PageHistory/types'
import type {
  VersionsResponseLegacy,
  VersionsResponse,
  ComparisonParams,
  VersionsParams,
  ComparisonResponseLegacy,
  ComparisonResponse,
  Change,
} from '@/components/ContentManager/EditorPage/PageHistory/types'

const CHANGES_MAP = versionOperator2ChangeSymbol

export type StoryPath = {
  draft: string
  live: string
}

export type State = {
  story: Story
  stories: Story[]
  releases: Release[]
  release: Release
  storyPaths: StoryPath
  optionCache: unknown
  contentErrors: ContentError[]
  unpublishedDependencies?: Record<number, UnpublishedDependencies>
  storyLoading: boolean
  isLargeStory: boolean
  contentLoading: boolean
  releaseId: number | null
  clipboard: Story[]
  presetFilters: PresetFilter[]
}

export type UnpublishedPayload = {
  story_ids: number[]
  release_id?: number
  lang: string
  story?: { content: BlockContent }
}

export type StoryPayload = {
  lang: string
  spaceId: number
  storyContent?: BlockContent
  storyId: number
  forceUpdate?: '1'
  story: Story
  baseVersionId?: number
}

export type CreateStoryPayload = {
  story: CreateContentForm
  credentials?: Record<string, string>
  spaceId: number
  releaseId?: number
}

export type SaveStoryPayload = {
  spaceId: string | number
  storyId: number
  story?: Partial<Story>
  storyContent?: BlockContent
  skipReload?: boolean
  favourite_for_user_ids?: number[]
  forceUpdate?: string
  softUpdated?: Partial<Story>
  releaseId?: number
  workflowId?: number
  baseVersionId?: number
}

export type ContentError = {
  _uid: BlockContent['_uid']
  component: BlockContent['component']
  displayName: FieldTypeSchema['display_name']
  // Human-readable string
  error: string
  key: keyof BlockSchema
  richTextParent?: Record<never, never>
  language?: string
}

export type FilterQueryOperator =
  | 'is'
  | 'in'
  | 'not_in'
  | 'like'
  | 'not_like'
  | 'any_in_array'
  | 'all_in_array'
  | 'gt_date'
  | 'lt_date'
  | 'gt_int'
  | 'lt_int'
  | 'gt_float'
  | 'lt_float'

/**
 * A query to filter stories with the logical AND operator
 * Each property is a key-value pair where the key is the name of the field and the value is an object that represents
 * a logical operation on that field's value.
 * The expression evaluates to `true` if all operators evaluate to `true`.
 *
 * TODO: confirm that you can perform multiple operations on the same field.
 */
export type FilterQueryAndExpression = {
  [field in string]?: {
    [operator in FilterQueryOperator]?: string
  }
}

/**
 * A query to filter stories with the logical OR operator.
 * The expression evaluates to `true` if any of the queries in the array evaluate to `true`.
 */
export type FilterQueryOrExpression = {
  __or?: FilterQuery[]
}

/**
 * A query to filter stories.
 * The properties in this object are matched against the story's fields with the logical AND operator.
 * The exception is the __or property, which is an array of other FilterQuery objects.
 *
 * TODO: confirm that this is a recursive type.
 */
export type FilterQuery = FilterQueryAndExpression & FilterQueryOrExpression

export type LoadStoriesQueryParams = {
  page?: number
  per_page?: number
  with_summary?: '1' | number
  story_only?: '1' | boolean
  search?: string | null
  contain_component?: string
  folder_only?: '1'
  with_parent?: number
  branch_id?: number
  /**
   * Comma-separated list of strings
   */
  by_slugs?: string
  filter_query?: FilterQuery | null
  sort_by?: string
  text_search?: string
  by_ids?: number[] | string
  by_uuids?: number[] | string // This is part of the openapi schema
  with_alts?: string
  // Stringified array of uids, or array of strings
  reference_search?: string | string[]
}

export type LoadStoriesParams = {
  spaceId: string | number
  apiParameters?: LoadStoriesQueryParams
  query?: string
  noCommit?: boolean
  signal?: AbortSignal
}

type ExportStoryPayload = {
  spaceId?: string | string[]
  storyId?: string | string[]
  fileExtension: string
  apiParameters: {
    lang_code: Language['code']
    export_lang: boolean
    version: number
    release_id?: number
  }
}

type ExportStoryResponse =
  | {
      page: string
      language: string
      url: string
      text_nodes: number
    }
  | string

type ImportStoryPayload = {
  spaceId?: string | string[]
  storyId?: string | string[]
  fileExtension?: string
  apiParameters: {
    data: string | ArrayBuffer | null
    lang_code: string
    version: number
    release_id?: number
  }
}

export const useStories = defineStore('stories', {
  state: (): State => ({
    // @ts-expect-error -- empty story object is valid during initialization/reset
    story: {},
    stories: [],
    releases: [],
    // @ts-expect-error -- empty release object is valid during initialization/reset
    release: {},
    storyPaths: {
      draft: '',
      live: '',
    },
    optionCache: {},
    contentErrors: [],
    unpublishedDependencies: undefined,
    storyLoading: false,
    isLargeStory: false,
    contentLoading: false,
    releaseId: null,
    clipboard: [],
    presetFilters: [],
  }),
  getters: {
    getOptionCache: (state: State): unknown => state.optionCache,
    getStory: (state: State): Story => state.story,
    getStories: (state: State): Story[] => state.stories,
    getStoryPaths: (state: State): StoryPath => {
      const space = useSpace().space

      const shouldEncodePreviewUrls = Boolean(space.encode_preview_urls)
      const draft = shouldEncodePreviewUrls
        ? state.storyPaths.draft.replace(/\[/g, '%5B').replace(/\]/g, '%5D')
        : state.storyPaths.draft

      return {
        ...state.storyPaths,
        draft,
      }
    },
    getContentErrors: (state: State): ContentError[] => state.contentErrors,
    hasContentErrors: (state: State): boolean => state.contentErrors.length > 0,
    hasUnpublishedDependencies:
      (state: State) =>
      (storyId: number): boolean => {
        return Boolean(
          state.unpublishedDependencies?.[storyId]?.unpublished_stories.some(
            (story) => !story.published,
          ),
        )
      },
    getStoryLoading: (state: State): boolean => state.storyLoading,
    getContentLoading: (state: State): boolean => state.contentLoading,
    getReleaseId: (state: State): number | null => state.releaseId || null,
    getReleases: (state: State): Release[] => state.releases || [],
    getIsLargeStory: (state: State): boolean => state.isLargeStory,
    getRelease: (state: State): Release => state.release,
    getReleaseById:
      (state: State) =>
      (releaseId: number): Release | undefined =>
        state.releases.find((release) => release.id === releaseId),
    getClipboardStories: (state: State): Story[] => state.clipboard,
  },
  actions: {
    resetStory(): void {
      // @ts-expect-error -- empty story object is valid during initialization/reset
      this.story = {}
      this.optionCache = {}
    },

    resetContentErrors(): void {
      this.contentErrors = []
    },

    setContentErrors(errors: ContentError[]): void {
      if (isArray(errors)) {
        this.contentErrors = errors
      }
    },

    unsetUnpublishedDependencies(storyId: number): void {
      if (this.unpublishedDependencies === undefined) {
        return
      }

      if (this.unpublishedDependencies[storyId]) {
        delete this.unpublishedDependencies[storyId]
      }

      if (Object.keys(this.unpublishedDependencies).length === 0) {
        this.unpublishedDependencies = undefined
      }
    },

    setOptionCache(value: unknown): void {
      this.optionCache = value
    },

    setIsLargeStory(value: boolean): void {
      this.isLargeStory = value
    },

    setStory(story: Story): void {
      if (!story) {
        return
      }
      if (isEmpty(story?.content)) {
        this.story = story
      } else {
        this.story = Object.assign({}, story, { content: null })
      }
    },

    setStorySlugs(slugs: TranslatedSlugs[]): TranslatedSlugs[] {
      return (this.story.translated_slugs = slugs)
    },

    setStories(stories: Story[]): Story[] {
      return (this.stories = stories)
    },

    setStoryPaths(storyPaths: StoryPath): StoryPath {
      return (this.storyPaths = storyPaths)
    },

    updateStory<K extends keyof Story>({
      key,
      value,
    }: {
      key: K
      value: Story[K]
    }) {
      this.story[key] = value
    },

    setStoryLoading(value: boolean): boolean {
      return (this.storyLoading = value)
    },

    setContentLoading(value: boolean): boolean {
      return (this.contentLoading = value)
    },

    updateReleaseId(value: number | null): undefined {
      if (value !== 0 && value !== null && value !== undefined) {
        this.releaseId = value
        return
      }

      this.releaseId = null
    },

    setReleases(releases: Release[]): void {
      this.releases = releases
    },

    setRelease(release: Release): void {
      this.release = release
    },

    clearReleases(): unknown {
      return (this.releases = [])
    },

    clearRelease(): unknown {
      // @ts-expect-error -- empty story object is valid during initialization/reset
      return (this.release = {})
    },

    setClipboardStories(stories: Story[]): Story[] {
      return (this.clipboard = [...stories])
    },

    clearClipboardStories(): unknown {
      return (this.clipboard = [])
    },

    async loadStories(
      payload: LoadStoriesParams,
    ): Promise<AxiosResponse<StoriesResponse>> {
      const {
        spaceId,
        apiParameters,
        query = '',
        noCommit = false,
        signal,
      } = payload

      const response = await client.get(`/spaces/${spaceId}/stories${query}`, {
        params: apiParameters,
        region: useSpace().region,
        signal,
      })

      if (!noCommit) {
        this.setStories(response.data.stories)
      }

      return response
    },

    async loadStory(payload: {
      spaceId: string | number
      storyId: number
      withSummary?: boolean
      ignoreSave?: boolean
    }): Promise<Story> {
      const queryParams = {} as {
        release_id?: number
        branch_id?: number | null
      }

      if (this.releaseId !== null) {
        queryParams.release_id = this.releaseId
      }

      if (usePipelinesStore().pipelineId !== null) {
        // branch_id property is required by the backend
        queryParams.branch_id = usePipelinesStore().pipelineId
      }

      const { spaceId, storyId, withSummary = true } = payload
      if (!spaceId) {
        throw new Error('Space ID parameter is required')
      }
      const spaceIdNumber = numberFromString(spaceId.toString())

      if (spaceIdNumber === undefined) {
        throw new Error(
          'Space ID parameter is converted to wrong numeric value',
        )
      }

      const baseUrl = getBaseUrlForSpaceId(spaceIdNumber)
      if (!baseUrl) {
        throw new Error('Base URL not found')
      }
      const openApiMapiClient = managementApiClient(baseUrl)

      const result = await handleRequestOpenApi({
        apiCall: () =>
          openApiMapiClient.GET('/v1/spaces/{space_id}/stories/{id}', {
            params: {
              path: {
                space_id: spaceIdNumber,
                id: storyId,
              },
              query: queryParams,
            },
          }),
      })

      if (result.status !== 'success') {
        throw new Error(`Failed to load story ${storyId}`)
      }

      if (withSummary) {
        return this.loadStoryWithoutContent({
          params: queryParams,
          spaceId,
          storyId,
        })
      } else if (!payload.ignoreSave) {
        this.setContentLoading(true)
      }

      const { response, data } = result

      if (payload.ignoreSave) {
        return data.story
      }

      const contentLength = response.headers.get('content-length') ?? '0'

      const contentLengthNumber = numberFromString(contentLength) ?? 0
      const contentLengthInMb = contentLengthNumber / 1000000

      if (contentLengthInMb > 1.5) {
        this.setIsLargeStory(true)
      }

      this.setStory(data.story)
      this.setContentLoading(false)

      return data.story
    },

    async loadStoryWithoutContent(payload: {
      spaceId: string | number
      storyId: number
      params: { release_id?: number; branch_id?: number | null }
    }): Promise<Story> {
      this.setStoryLoading(true)

      const { spaceId, storyId, params } = payload

      const { data } = await client.get<StoriesResponse>(
        `spaces/${spaceId}/stories?by_ids=${storyId}`,
        {
          params,
          region: useSpace().region,
        },
      )

      const story = data.stories[0]
      // @ts-expect-error -- empty story object is valid during initialization/reset
      this.setStory(story ?? {}) // TODO: fix to Story, in upcoming PR's
      this.setStoryLoading(false)
      // @ts-expect-error -- empty story object is valid during initialization/reset
      return story || {}
    },

    async loadStorySimple(payload: {
      spaceId: string | number
      storyId: number
      clientParams?: {
        uuid?: string | number
        release_id?: number
        with_summary?: number
      }
    }): Promise<Story> {
      const { spaceId, storyId, clientParams } = payload

      if (!spaceId) {
        throw new Error('Space Id is required')
      }
      const spaceIdNumber = numberFromString(spaceId.toString())

      if (spaceIdNumber === undefined) {
        throw new Error(
          'Space ID parameter is converted to wrong numeric value',
        )
      }
      const baseUrl = getBaseUrlForSpaceId(spaceIdNumber)
      if (!baseUrl) {
        throw new Error('Base URL not found')
      }
      const openApiMapiClient = managementApiClient(baseUrl)

      const queryParams: {
        release_id?: number
        uuid?: string
        with_summary?: number
      } = {}

      if (clientParams?.release_id) {
        queryParams.release_id = clientParams.release_id
      }

      if (clientParams?.uuid) {
        queryParams.uuid = String(clientParams.uuid)
      }

      if (clientParams?.with_summary) {
        queryParams.with_summary = clientParams.with_summary
      }

      const result = await handleRequestOpenApi({
        apiCall: () =>
          openApiMapiClient.GET('/v1/spaces/{space_id}/stories/{id}', {
            params: {
              path: {
                space_id: spaceIdNumber,
                id: storyId,
              },
              query: queryParams,
            },
          }),
      })

      if (result.status !== 'success') {
        throw new Error(`Failed to load story ${storyId}`)
      }

      return result.data.story
    },

    /**
     * Fetches a stories by UUIDs. This function will not throw an exception: if the request fails, it will return an `Error`.
     * @param payload
     */
    async fetchStoriesByUuids(payload: {
      uuids: string[]
    }): Promise<Omit<Story, 'content'>[] | Error> {
      const { uuids } = payload
      if (uuids.length === 0) {
        return []
      }
      const spaceId = useSpace().space.id
      const region = useSpace().region
      try {
        const res = await client.get(`spaces/${spaceId}/stories`, {
          params: {
            by_uuids: unique(uuids).join(','),
          },
          region,
        })
        // TODO validate response
        return res.data.stories
      } catch (e) {
        return e instanceof Error
          ? e
          : new Error('Unknown error fetching stories')
      }
    },

    async countReferences({
      terms,
      spaceId,
    }: {
      terms: string[]
      spaceId: number
    }): Promise<ReferenceCountsResponse> {
      const { region } = useSpace()
      const chunks = chunk(terms, 100)

      const promisesThrottle = new Throttle({
        requestsPerSecond: 3,
      })

      const tasks = chunks.map((chunk) => async () => {
        const { data: references } = await client.get(
          `/spaces/${spaceId}/stories/reference_counts`,
          {
            params: {
              terms: chunk,
            },
            region,
          },
        )
        return references
      })

      const results = (await promisesThrottle.addAll(
        tasks,
      )) as ReferenceCountsResponse[]

      return results.reduce(
        (acc, references) => ({ ...acc, ...references }),
        {},
      )
    },

    async loadBreadcrumbs({
      spaceId,
      params,
    }: {
      spaceId: string | number
      params: Record<string, unknown>
    }): Promise<Breadcrumb[]> {
      const { data } = await client.get<BreadcrumbsResponse>(
        `spaces/${spaceId}/breadcrumbs`,
        {
          params,
          region: useSpace().region,
        },
      )

      return data.breadcrumbs || []
    },

    async deleteStory({
      spaceId,
      storyId,
    }: {
      spaceId: string | number
      storyId: number
    }): Promise<void> {
      return client.delete(`spaces/${spaceId}/stories/${storyId}`, {
        region: useSpace().region,
      })
    },

    async createStory(payload: CreateStoryPayload): Promise<Story> {
      const { story, credentials, spaceId, releaseId } = payload
      const response = await client.post<{ story: Story }>(
        `spaces/${spaceId}/stories`,
        story,
        {
          params: { ...credentials, release_id: releaseId },
          region: useSpace().region,
        },
      )

      if (hasProp(story, 'is_folder', isBoolean)) {
        return response.data.story
      }

      this.setStory(response.data.story) // TODO: fix to Story, in upcoming PR's
      return response.data.story
    },

    async savePartialStory(payload: StoryPayload): Promise<Story> {
      const { spaceId, storyId, story } = payload

      const apiParameters = {
        lang: useSpace().lang,
        story,
      }

      const { data } = await client.put<{ story: Story }>(
        `spaces/${spaceId}/stories/${storyId}/partial_update`,
        apiParameters,
        {
          region: useSpace().region,
        },
      )

      this.setStory(data.story)
      return data.story
    },

    async saveStory(payload: SaveStoryPayload): Promise<Story> {
      const {
        spaceId,
        storyId,
        story,
        storyContent,
        skipReload,
        forceUpdate,
        softUpdated,
        releaseId,
        workflowId,
        favourite_for_user_ids,
        baseVersionId,
      } = payload
      const sourceStory = story || this.story
      const mergedStory = {
        ...sourceStory,
        ...softUpdated,
      }

      const queryParams: {
        lang?: string
        release_id?: number
        force_update?: boolean
        publish?: boolean
        base_version_id?: number
      } = {
        lang: useSpace().lang ?? undefined,
        force_update: forceUpdate ? true : undefined,
        base_version_id: baseVersionId,
      }

      if (storyContent && !isEmpty(storyContent)) {
        mergedStory.content = storyContent
      }

      if (workflowId) {
        // @ts-expect-error -- workflow_id is a property of Story, not part of openAPI spec
        mergedStory.workflow_id = workflowId
      }

      if (favourite_for_user_ids) {
        mergedStory.favourite_for_user_ids = favourite_for_user_ids
      }

      if (releaseId !== undefined) {
        // @ts-expect-error -- release_id is a property of Story, not part of openAPI spec
        mergedStory.release_id = releaseId
        // @ts-expect-error -- publish is a property of Story, not part of openAPI spec
        mergedStory.publish = '1'

        queryParams.release_id = releaseId
      }

      //The OpenAPI types expect UpdateStoryRequest directly, but the actual API requires it wrapped in "story"
      const body = {
        story: mergedStory,
      }

      if (!spaceId) {
        throw new Error('Space ID parameter is required')
      }
      const spaceIdNumber = numberFromString(spaceId.toString())

      if (spaceIdNumber === undefined) {
        throw new Error(
          'Space ID parameter is converted to wrong numeric value',
        )
      }
      const baseUrl = getBaseUrlForSpaceId(spaceIdNumber)
      if (!baseUrl) {
        throw new Error('Base URL not found')
      }
      const openApiMgmClient = managementApiClient(baseUrl)

      const result = await handleRequestOpenApi({
        apiCall: () =>
          openApiMgmClient.PUT('/v1/spaces/{space_id}/stories/{id}', {
            params: {
              path: {
                space_id: spaceIdNumber,
                id: storyId,
              },
              query: queryParams,
            },
            headers: {
              'Content-type': 'application/json',
            },
            // @ts-expect-error - OpenAPI types expect UpdateStoryRequest directly, but API requires it wrapped in "story"
            body,
          }),
        expectedErrors: new Map([[422, (backendError) => backendError]]),
      })

      if (result.status !== 'success') {
        const isExpectedError = result.status === 'expectedError'

        const errorData = isExpectedError
          ? result.backendError
          : `Failed to save story ${storyId}`

        const errorToBeThrown = {
          ...result,
          response: {
            data: errorData,
            status: isExpectedError
              ? result.statusCode
              : result.response?.status,
          },
        }
        throw errorToBeThrown
      }

      if (!skipReload && !softUpdated) {
        this.setStory(result.data.story)
      }

      return result.data.story
    },

    async restoreDeletedStory(payload: {
      spaceId: string
      storyId: number
    }): Promise<void> {
      const { spaceId, storyId } = payload

      return client.get(`spaces/${spaceId}/stories/${storyId}/undelete`, {
        region: useSpace().region,
      })
    },

    async bulkRestoreDeletedStories(payload: {
      spaceId: string
      storyIds: number[]
    }): Promise<{ stories: BulkRestoreResponse }> {
      const { spaceId, storyIds } = payload

      const response = await client.post<{ stories: BulkRestoreResponse }>(
        `spaces/${spaceId}/stories/bulk_undelete`,
        {
          story_ids: storyIds,
        },
        {
          region: useSpace().region,
        },
      )

      return response.data
    },

    async updateDeletedContent(payload: { spaceId: string; story: Story }) {
      const { spaceId, story } = payload
      const apiParameters = {
        story: {
          name: story.name,
          slug: story.slug,
        },
      }

      return client.put(
        `spaces/${spaceId}/stories/${story.id}/deleted_update`,
        apiParameters,
        {
          region: useSpace().region,
        },
      )
    },

    async duplicateContent(payload: { spaceId: string; story: Story }) {
      const { spaceId, story } = payload
      const apiParameters = {
        story,
      }

      return client.put(
        `spaces/${spaceId}/stories/${story.id}/duplicate`,
        apiParameters,
        {
          region: useSpace().region,
        },
      )
    },

    async publishStory({
      spaceId,
      storyId,
      lang = '',
      forceUpdate,
      storyContent,
      story,
      baseVersionId,
    }: StoryPayload): Promise<SafeAxiosResponse<{ story: Story }>> {
      const apiParameters: {
        lang: string
        story: Story
        publish: '1'
        release_id?: number
        force_update?: '1'
      } = {
        lang,
        story,
        publish: '1',
      }

      if (storyContent && !isEmpty(storyContent)) {
        apiParameters.story.content = storyContent
      }

      if (this.releaseId !== null) {
        apiParameters.release_id = this.releaseId
      }

      if (baseVersionId !== undefined) {
        apiParameters.story.base_version_id = baseVersionId
      }

      if (forceUpdate) {
        apiParameters.force_update = forceUpdate
      }

      const response = await handleRequest({
        apiCall: (): Promise<AxiosResponse<{ story: Story }>> =>
          client.put(`spaces/${spaceId}/stories/${storyId}`, apiParameters, {
            region: useSpace().region,
          }),
        expectedErrors: new Map([[422, (backendError) => backendError]]),
      })

      return response
    },

    async unpublishStory(payload: {
      spaceId: string
      storyId: number
      lang?: string
      releaseId?: number
      ignoreSave?: boolean
    }): Promise<Story> {
      const { spaceId, storyId, lang = '', releaseId } = payload
      const langParam = lang.length ? `lang=${lang}` : ''
      const releaseIdParam = releaseId ? `release_id=${releaseId}` : ''

      let apiParameters = ''

      if (langParam) {
        apiParameters = `?${langParam}`
      }

      if (releaseIdParam) {
        apiParameters = `?${releaseIdParam}`
      }

      if (langParam && releaseIdParam) {
        apiParameters = `?${langParam}&${releaseIdParam}`
      }

      const { data } = await client.get<{ story: Story }>(
        `spaces/${spaceId}/stories/${storyId}/unpublish${apiParameters}`,
        {
          region: useSpace().region,
        },
      )

      if (payload.ignoreSave) {
        return data.story
      }

      this.setStory(data.story)

      return data.story
    },

    async discardChanges(payload: {
      spaceId: string | number
      storyId: number
      releaseId: number
      ignoreSave?: boolean
    }) {
      const { spaceId, storyId, releaseId } = payload

      const { data } = await client.get<{ story: Story }>(
        `spaces/${spaceId}/stories/${storyId}/discard_changes?release_id=${releaseId}`,
        {
          region: useSpace().region,
        },
      )

      if (payload.ignoreSave) {
        return data.story
      }

      return this.setStory(data.story)
    },

    /**
     * Legacy version fetching
     *
     * @param payload
     * @returns
     */
    // TODO: when the 'enhanced-history' flag will be removed from src/components/ContentManager/EditorPage/EditorHeader/EditorHeader.vue,
    // remove this function as well as this file src/components/ContentManager/EditorPage/PageHistory/index.vue
    // and the folder src/components/ContentManager/EditorPage/PageHistory/components/Legacy
    async fetchLegacyVersions(payload: {
      spaceId: number
      storyId: number
      apiParameters: VersionsParams
    }): Promise<AxiosResponse<VersionsResponseLegacy>> {
      const { spaceId, storyId, apiParameters } = payload

      const response = await client.get(
        `/spaces/${spaceId}/stories/${storyId}/versions`,
        {
          params: apiParameters,
          region: useSpace().region,
        },
      )

      return response
    },

    /**
     * Legacy version fetching
     *
     * @param payload
     * @returns
     */
    async fetchAllowedLegacyVersions(payload: {
      spaceId: number
      storyId: number
      apiParameters: VersionsParams
    }): Promise<AxiosResponse<VersionsResponseLegacy>> {
      const { spaceId, storyId, apiParameters } = payload

      const response = await client.get(`/spaces/${spaceId}/versions`, {
        params: {
          model: 'stories',
          model_id: storyId,
          versions_v2: true,
          ...apiParameters,
        },
        region: useSpace().region,
      })

      return response
    },

    /**
     * Fetches versions with the new API
     *
     * @param payload
     * @returns
     */
    async fetchVersions(
      payload: VersionsParams,
    ): Promise<AxiosResponse<VersionsResponse>> {
      const { spaceId, region } = useSpace()

      const response = await client.get(`/spaces/${spaceId}/story_versions`, {
        params: payload,
        region,
      })

      return response
    },

    /**
     * Fetches the comparison of two versions of a story
     * The response an array of the changes between the two versions
     * To also include the full content of the story, set the `versions_v2` parameter to `true`
     *
     * @param payload
     * @returns
     */

    // TODO: when the 'enhanced-history' flag will be removed from src/components/ContentManager/EditorPage/EditorHeader/EditorHeader.vue,
    // remove the code that return ComparisonResponseLegacy in this function as well as this file src/components/ContentManager/EditorPage/PageHistory/index.vue
    // and the folder src/components/ContentManager/EditorPage/PageHistory/components/Legacy
    async fetchComparison(payload: {
      storyId: number
      apiParameters: ComparisonParams
    }): Promise<ComparisonResponseLegacy | ComparisonResponse> {
      const { spaceId, region } = useSpace()
      const { storyId, apiParameters } = payload

      const { data } = await client.get(
        `/spaces/${spaceId}/stories/${storyId}/compare`,
        {
          params: {
            ...apiParameters,
            full_changes: apiParameters.legacy ? undefined : true,
          },
          region,
        },
      )

      if (apiParameters.legacy) {
        return data
      } else {
        const mappedChanges: Change[] =
          data.changes?.reduce(
            (acc: Change[], change: ComparisonResponseLegacy) => {
              const [operation, field, previousValue, nextValue] = change
              acc.push({
                operation: CHANGES_MAP[operation],
                field,
                previousValue: operation === '+' ? undefined : previousValue,
                nextValue: operation === '+' ? previousValue : nextValue,
              })
              return acc
            },
            [],
          ) ?? []

        return {
          changes: mappedChanges,
          full: data.full,
        }
      }
    },

    async restoreVersion(payload: {
      spaceId: number
      storyId: number
      apiParameters: {
        version: number
        version_v2?: boolean
        release_id?: number
      }
    }): Promise<Story> {
      const { spaceId, storyId, apiParameters } = payload

      const { data } = await client.get<{ story: Story }>(
        `/spaces/${spaceId}/stories/${storyId}/restore_with`,
        {
          params: apiParameters,
          region: useSpace().region,
        },
      )

      this.setStory(data.story)

      return data.story
    },

    syncRouteParamsWithStories(params: {
      releaseId?: string | number
      pipelineId?: string | number
    }): Promise<boolean> {
      this.updateReleaseId(numberFromString(String(params.releaseId)) || null)
      usePipelinesStore().updatePipelineId(
        numberFromString(String(params.pipelineId)) || null,
      )

      return Promise.resolve(true)
    },

    async exportStory(
      payload: ExportStoryPayload,
    ): Promise<SafeAxiosResponse<ExportStoryResponse>> {
      const { spaceId, storyId, fileExtension, apiParameters } = payload

      const response = await handleRequest({
        apiCall: (): Promise<AxiosResponse<ExportStoryResponse>> =>
          client.get(
            `spaces/${spaceId}/stories/${storyId}/export.${fileExtension}`,
            {
              params: apiParameters,
              region: useSpace().region,
            },
          ),
      })

      return response
    },

    async importStory(
      payload: ImportStoryPayload,
    ): Promise<SafeAxiosResponse<{ story: Story }>> {
      const { spaceId, storyId, fileExtension, apiParameters } = payload

      const response = await handleRequest({
        apiCall: (): Promise<AxiosResponse<{ story: Story }>> =>
          client.put(
            `/spaces/${spaceId}/stories/${storyId}/import.${fileExtension}`,
            apiParameters,
            {
              region: useSpace().region,
            },
          ),
      })

      if (response.status === 'success') {
        this.setStory(response.data.story) // TODO: fix to Story this in upcoming PR's
      }

      return response
    },

    async createRelease(payload: {
      spaceId: string | number
      release: Partial<Release>
    }): Promise<Release> {
      const { spaceId, release } = payload

      const { data } = await client.post<{ release: Release }>(
        `/spaces/${spaceId}/releases/`,
        { release },
        {
          region: useSpace().region,
        },
      )

      this.loadReleases(payload)

      return data.release
    },

    async loadReleases(payload: {
      spaceId: string | number
      params?: Record<string, unknown>
    }): Promise<Release[]> {
      const { spaceId, params } = payload
      const { data } = await client.get<Releases>(
        `/spaces/${spaceId}/releases/`,
        {
          params,
          region: useSpace().region,
        },
      )

      this.setReleases(data.releases)

      return data.releases
    },

    async loadRelease(payload: {
      spaceId: number
      releaseId: number
    }): Promise<Release> {
      const { spaceId, releaseId } = payload

      const { data } = await client.get(
        `/spaces/${spaceId}/releases/${releaseId}`,
        {
          region: useSpace().region,
        },
      )

      this.setRelease(data.release)

      return data.release
    },

    async updateRelease({
      spaceId,
      ...payload
    }: {
      spaceId: string | number
      release: Partial<Release>
      do_release?: boolean
    }): Promise<Release> {
      const { data } = await client.put<{ release: Release }>(
        `/spaces/${spaceId}/releases/${payload.release.id}`,
        payload,
        {
          region: useSpace().region,
        },
      )

      this.loadReleases({ spaceId })

      return data.release
    },

    async deleteRelease(payload: {
      spaceId: string | number
      releaseId: number
    }): Promise<void> {
      const { spaceId, releaseId } = payload

      await client.delete(`/spaces/${spaceId}/releases/${releaseId}`, {
        region: useSpace().region,
      })

      this.loadReleases(payload)
    },

    async checkReleaseConflicts(payload: {
      spaceId: string | number
      releaseId: number
    }): Promise<{ has_conflicts: boolean }> {
      const { spaceId, releaseId } = payload

      const { data } = await client.get<{ has_conflicts: boolean }>(
        `/spaces/${spaceId}/releases/${releaseId}/conflict_check`,
        {
          region: useSpace().region,
        },
      )

      return data
    },

    async getReleasesForAPipeline({
      spaceId,
      sourceId,
    }: {
      spaceId: string | number
      sourceId?: number
    }): Promise<Release[]> {
      let basePath = `spaces/${spaceId}/releases/`

      if (sourceId) {
        basePath = `${basePath}?branch_id=${sourceId}`
      }

      const { data } = await client.get(basePath, {
        region: useSpace().region,
      })

      return data.releases
    },

    /**
     *
     * @param {Object} payload
     * @param {number} payload.spaceId the space id number
     * @param {number[]} payload.storyIds an array of story ids numbers
     * @param {string} payload.content it's a stringified content of the story
     * @param {number} payload.releaseId the release id number
     * @param {string} payload.lang the language code
     */
    async getAndSetUnpublishedDependencies({
      spaceId,
      storyIds,
      content,
      releaseId,
      lang,
      storyId,
    }: {
      spaceId: number
      storyIds: number[]
      content?: BlockContent
      releaseId: number
      lang: string
      storyId: number
    }): Promise<void> {
      const params: UnpublishedPayload = {
        story_ids: storyIds,
        lang,
      }
      if (releaseId) {
        params.release_id = releaseId
      }
      if (content) {
        params.story = { content }
      }
      const dependencies = await client
        .post(
          `/spaces/${spaceId}/stories/unpublished_dependencies`,
          {
            ...params,
          },
          {
            region: useSpace().region,
          },
        )
        .then((response) => response.data as UnpublishedDependencies)
        .catch(() => {
          console.error('Error fetching unpublished dependencies')
        })

      const removeCurrentStory = (
        dependencies: UnpublishedDependencies,
      ): UnpublishedDependencies => {
        return {
          ...dependencies,
          stories: dependencies.stories.map((story) => {
            return {
              ...story,
              fields: story.fields?.filter(
                (field) => field.unpublished_story !== storyId,
              ),
            }
          }),
          unpublished_stories: dependencies.unpublished_stories.filter(
            ({ id }) => id !== storyId,
          ),
        }
      }

      if (dependencies) {
        this.unpublishedDependencies ??= {}
        this.unpublishedDependencies[storyId] = removeCurrentStory(dependencies)
      }
    },

    async validateSlugs(payload: {
      spaceId: number
      story: {
        is_startpage: boolean
        parent_id: number
        slug?: string
        translated_slugs_attributes?: {
          lang: string
          slug: string
        }[]
      }
      signal?: AbortSignal
    }): Promise<AxiosResponse> {
      const { spaceId, story, signal } = payload

      return await client.post(
        `spaces/${spaceId}/stories/validate_slugs`,
        { story },
        {
          region: useSpace().region,
          signal,
        },
      )
    },
    async translateStory({
      spaceId,
      storyId,
      aiTranslationCode,
      code,
      overwrite = false,
      releaseId,
      signal,
    }: {
      spaceId: number
      storyId: number
      aiTranslationCode: string
      code: string
      overwrite?: boolean
      releaseId?: number
      signal?: AbortSignal
    }): Promise<{ background_task_id: number }> {
      const { data }: AxiosResponse<{ background_task_id: number }> =
        await client.put(
          `spaces/${spaceId}/stories/${storyId}/ai_translate`,
          {
            lang: aiTranslationCode,
            code,
            release_id: releaseId,
            overwrite,
          },
          {
            region: useSpace().region,
            signal,
          },
        )

      return data
    },

    async moveSingleStory({
      spaceId,
      storyId,
      payload,
    }: {
      spaceId: number
      storyId: number
      payload: {
        to_folder?: number | string
        after_id?: number | string
      }
    }): Promise<void> {
      await client.put(`spaces/${spaceId}/stories/${storyId}/move`, payload, {
        region: useSpace().region,
      })
    },

    async moveMultipleStories({
      spaceId,
      payload,
    }: {
      spaceId: number
      payload: {
        story_ids: number[]
        to_folder?: number | string
        after_id?: number | string
      }
    }): Promise<void> {
      await client.post(`spaces/${spaceId}/stories/bulk_move`, payload, {
        region: useSpace().region,
      })
    },
  },
})
